var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { DateTime } from "luxon";
import { ResourceDisplayKind } from "../../../models/aggregation/aggregation.model";
import { ReservationStatus } from "../../../models/reservation/reservation.model";
import { SectorType } from "../../../models/sector/sector.model";
import { getEventColorBasedOnStatus } from "../../../utils/color.util";
import { RESOURCE_ELEMENT_DEFAULT_COLORS } from "../../uiElements/ResourceElement";
var OPTION_COLOR = '#9575CD';
var STRUCK_STATUSES = [ReservationStatus.CANCELLED, ReservationStatus.NO_SHOW];
var HAPPENED_STATUSES = [ReservationStatus.CONFIRMED, ReservationStatus.ACTIVE, ReservationStatus.COMPLETED];
var MEAL_LABELS = { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner' };
export var statusColor = function (status) { var _a; return (_a = getEventColorBasedOnStatus(status)) !== null && _a !== void 0 ? _a : (status === ReservationStatus.OPTION ? OPTION_COLOR : 'divider'); };
/*
    The year only a year or more away: each group runs one way in time (Upcoming ahead, Other back), so a nearer date
    can't be misread ("05.01" under Upcoming in October is next January). Shorter dates keep the date column narrow.
*/
var needsYear = function (day) { return Math.abs(day.diffNow('months').months) >= 12; };
var formatDay = function (day) { return day.toFormat(needsYear(day) ? 'dd.MM.yyyy' : 'dd.MM'); };
export var formatVisitDates = function (start, end) {
    if (start.hasSame(end, 'day'))
        return formatDay(start);
    if (start.hasSame(end, 'month'))
        return "".concat(start.toFormat('dd'), ".\u2013").concat(formatDay(end));
    if (start.hasSame(end, 'year') || !needsYear(start))
        return "".concat(start.toFormat('dd.MM'), ".\u2013").concat(formatDay(end));
    return "".concat(formatDay(start), "\u2013").concat(formatDay(end));
};
var startOf = function (reservation) { return DateTime.fromJSDate(new Date(reservation.startDate)); };
var endOf = function (reservation) {
    return reservation.endDate ? DateTime.fromJSDate(new Date(reservation.endDate)) : startOf(reservation);
};
var isStruck = function (reservation) {
    return !!reservation.status && STRUCK_STATUSES.includes(reservation.status);
};
export var hasEnded = function (reservation) { return endOf(reservation) < DateTime.now(); };
/** Over and not cancelled: what the sector lists' "Other" group lists. */
export var happened = function (reservation) {
    return endOf(reservation) < DateTime.now() && !!reservation.status && HAPPENED_STATUSES.includes(reservation.status);
};
export var isDuring = function (reservation, stay) {
    var day = startOf(reservation).toISODate();
    return !!day && !!stay.startDay && !!stay.endDay && day >= stay.startDay && day <= stay.endDay;
};
var nightsBetween = function (start, end) {
    return Math.max(Math.round(end.startOf('day').diff(start.startOf('day'), 'days').days), 1);
};
// Staff know their rate codes, so a rate shows as written; only empty, "NULL" and generated PMS_ codes are hidden.
var usableCode = function (code) {
    var value = code === null || code === void 0 ? void 0 : code.trim();
    return value && !/^null$/i.test(value) && !/^PMS_/i.test(value) ? value : undefined;
};
// The meal-plan meal, else the start time (before 11 breakfast, before 16 lunch, then dinner): restaurant services are generic.
var mealOf = function (reservation) {
    var period = reservation.eatPeriod;
    if (period === 'breakfast' || period === 'lunch' || period === 'dinner')
        return period;
    var hour = startOf(reservation).hour;
    return hour < 11 ? 'breakfast' : hour < 16 ? 'lunch' : 'dinner';
};
// Spa services often carry their length in the name ("Hot stone 50"); it goes when it matches the booking.
var withoutMinutes = function (name, minutes) {
    var match = name.match(/^(.*\S)\s+(\d{2,3})\s*(?:min\.?|minutes|minuten|')?$/i);
    return match && Number(match[2]) === minutes ? match[1] : name;
};
var resourceColor = function (resource) { var _a, _b, _c; return (_c = (_a = resource.tagColor) !== null && _a !== void 0 ? _a : (_b = resource.parent) === null || _b === void 0 ? void 0 : _b.tagColor) !== null && _c !== void 0 ? _c : RESOURCE_ELEMENT_DEFAULT_COLORS[resource.kind]; };
var ofKind = function (reservation, kind) {
    return reservation.resources.filter(function (resource) { var _a; return resource.kind === kind && ((_a = resource.name) === null || _a === void 0 ? void 0 : _a.trim()); });
};
var resourceLabel = function (resources, prefix, color) {
    if (!resources.length)
        return undefined;
    var text = resources.map(function (resource) { return resource.name.trim(); }).join(' + ');
    return {
        color: color !== null && color !== void 0 ? color : resourceColor(resources[0]),
        text: text,
        title: prefix ? "".concat(prefix, ": ").concat(text) : text
    };
};
var restaurantTables = function (reservation) {
    var _a, _b, _c, _d, _e, _f, _g;
    var tables = ofKind(reservation, ResourceDisplayKind.RESTAURANT_TABLE);
    var restaurant = reservation.resources.find(function (resource) { return resource.kind === ResourceDisplayKind.RESTAURANT; });
    var restaurantName = (_d = ((_a = restaurant === null || restaurant === void 0 ? void 0 : restaurant.name) !== null && _a !== void 0 ? _a : (_c = (_b = tables[0]) === null || _b === void 0 ? void 0 : _b.parent) === null || _c === void 0 ? void 0 : _c.name)) === null || _d === void 0 ? void 0 : _d.trim();
    return resourceLabel(tables, restaurantName, (_g = (_f = (_e = tables[0]) === null || _e === void 0 ? void 0 : _e.parent) === null || _f === void 0 ? void 0 : _f.tagColor) !== null && _g !== void 0 ? _g : restaurant === null || restaurant === void 0 ? void 0 : restaurant.tagColor);
};
var placeKey = function (place) { var _a; return (_a = place === null || place === void 0 ? void 0 : place.text) !== null && _a !== void 0 ? _a : ''; };
var toVisit = function (reservation, t) {
    var _a, _b, _c, _d, _e, _f, _g;
    var start = startOf(reservation);
    var end = endOf(reservation);
    var serviceName = ((_b = (_a = reservation.service) === null || _a === void 0 ? void 0 : _a.name) === null || _b === void 0 ? void 0 : _b.trim()) || ((_c = reservation.sector) === null || _c === void 0 ? void 0 : _c.name) || '';
    var what = serviceName;
    var label;
    switch ((_d = reservation.sector) === null || _d === void 0 ? void 0 : _d.sectorType) {
        case SectorType.ACCOMMODATION: {
            what = (_g = (_e = usableCode(reservation.mealCode)) !== null && _e !== void 0 ? _e : usableCode((_f = reservation.service) === null || _f === void 0 ? void 0 : _f.name)) !== null && _g !== void 0 ? _g : t('{{count}} nights', { count: nightsBetween(start, end) });
            var rooms = ofKind(reservation, ResourceDisplayKind.ACCOMMODATION);
            label = resourceLabel(rooms, t(rooms.length > 1 ? 'Rooms' : 'Room'));
            break;
        }
        case SectorType.RESTAURANT: {
            what = t(MEAL_LABELS[mealOf(reservation)]);
            label = restaurantTables(reservation);
            break;
        }
        case SectorType.SPA: {
            what = withoutMinutes(serviceName, Math.round(end.diff(start, 'minutes').minutes));
            label = resourceLabel(ofKind(reservation, ResourceDisplayKind.STAFF).slice(0, 1));
        }
    }
    return {
        id: reservation.id,
        status: reservation.status,
        dates: formatVisitDates(start, end),
        what: what,
        occasion: reservation.tableReservationEvent,
        sector: reservation.sector,
        label: label,
        past: end < DateTime.now().startOf('day'),
        struck: isStruck(reservation),
        subRows: [],
        start: start,
        end: end
    };
};
var mostCommon = function (values) {
    var _a;
    var counts = new Map();
    values.forEach(function (value) { var _a; return counts.set(value, ((_a = counts.get(value)) !== null && _a !== void 0 ? _a : 0) + 1); });
    return (_a = Array.from(counts.entries()).sort(function (a, b) { return b[1] - a[1]; })[0]) === null || _a === void 0 ? void 0 : _a[0];
};
/*
    The meals of one meal plan (same reservationGroupId and meal) as one row: their date range, the table used most,
    the bar in the next meal's status (the last one's once all are over). Exceptions (cancelled, no-show, another
    table) become sub-rows.
*/
var toSeriesVisit = function (meals, t) {
    var _a, _b;
    var sorted = __spreadArray([], meals, true).sort(function (a, b) { return startOf(a).toMillis() - startOf(b).toMillis(); });
    var visits = sorted.map(function (meal) { return toVisit(meal, t); });
    var first = visits[0];
    var last = visits[visits.length - 1];
    var kept = visits.filter(function (visit) { return !visit.struck; });
    var mainKey = (_a = mostCommon(kept.map(function (visit) { return placeKey(visit.label); }))) !== null && _a !== void 0 ? _a : '';
    var main = visits.find(function (visit) { return placeKey(visit.label) === mainKey; });
    var next = (_b = kept.find(function (visit) { return visit.end >= DateTime.now(); })) !== null && _b !== void 0 ? _b : last;
    return __assign(__assign({}, first), { id: "series-".concat(first.id), status: next.status, dates: formatVisitDates(first.start, last.end), label: main === null || main === void 0 ? void 0 : main.label, 
        // The data does not say which plan it is (half board…): services are generic and the group holds no stay.
        whatTitle: "".concat(t('Meal plan'), ": ").concat(t('{{count}} meals', { count: visits.length })), occasion: undefined, past: last.past, struck: kept.length === 0, subRows: visits
            .filter(function (visit) { return visit.struck || placeKey(visit.label) !== mainKey; })
            // A cancelled night at the usual table shows no table; one at another table shows where.
            .map(function (visit) { return (__assign(__assign({}, visit), { label: placeKey(visit.label) !== mainKey ? visit.label : undefined })); })
            .reverse(), start: first.start, end: last.end });
};
/**
 * The rows for a set of reservations: restaurant meal plans (same reservationGroupId and meal, 2+ meals) as one row
 * per meal, everything else one row each. `withSubRows` keeps the plan's exceptions as sub-rows (sector lists);
 * the profile list shows the plan's rows alone.
 */
export var buildVisits = function (reservations, t, withSubRows) {
    var series = new Map();
    var singles = [];
    reservations.filter(function (reservation) { return !!reservation.startDate; }).forEach(function (reservation) {
        var _a, _b;
        if (((_a = reservation.sector) === null || _a === void 0 ? void 0 : _a.sectorType) === SectorType.RESTAURANT && reservation.reservationGroupId) {
            var key = "".concat(reservation.reservationGroupId, "|").concat(mealOf(reservation));
            series.set(key, __spreadArray(__spreadArray([], ((_b = series.get(key)) !== null && _b !== void 0 ? _b : []), true), [reservation], false));
        }
        else {
            singles.push(reservation);
        }
    });
    var visits = singles.map(function (reservation) { return toVisit(reservation, t); });
    series.forEach(function (meals) {
        if (meals.length === 1) {
            visits.push(toVisit(meals[0], t));
        }
        else {
            var visit = toSeriesVisit(meals, t);
            visits.push(withSubRows ? visit : __assign(__assign({}, visit), { subRows: [] }));
        }
    });
    return visits;
};
export var byNewestEnd = function (a, b) { return b.end.toMillis() - a.end.toMillis(); };
